/**
 * Build a structured module dashboard payload from MCP / partner-portal API docs.
 * Used by the AI Assistant to render the PO / ASN / GRN / Sales / Shipment dashboard UI.
 */

const MODULE_META = {
  purchaseorders: {
    key: "PO",
    title: "Purchase Order Dashboard",
    recordNoun: "Purchase Order",
    kpiTotalLabel: "Total Purchase Orders",
    kpiSpendLabel: "Total Spend",
    kpiPendingLabel: "Orders Pending",
    kpiCompletedLabel: "Orders Completed",
    categoryTitle: "Spend by Category",
  },
  asns: {
    key: "ASN",
    title: "ASN Dashboard",
    recordNoun: "ASN",
    kpiTotalLabel: "Total ASNs",
    kpiSpendLabel: "Total ASN Value",
    kpiPendingLabel: "ASNs Pending",
    kpiCompletedLabel: "ASNs Completed",
    categoryTitle: "Value by Category",
  },
  grns: {
    key: "GRN",
    title: "Goods Receipt Dashboard",
    recordNoun: "Goods Receipt",
    kpiTotalLabel: "Total GRNs",
    kpiSpendLabel: "Total Received Qty",
    kpiPendingLabel: "GRNs Pending",
    kpiCompletedLabel: "GRNs Completed",
    categoryTitle: "Qty by Category",
  },
  salesorders: {
    key: "SO",
    title: "Sales Order Dashboard",
    recordNoun: "Sales Order",
    kpiTotalLabel: "Total Sales Orders",
    kpiSpendLabel: "Total Revenue",
    kpiPendingLabel: "Orders Pending",
    kpiCompletedLabel: "Orders Completed",
    categoryTitle: "Revenue by Category",
  },
  shipments: {
    key: "SHIPMENT",
    title: "Shipment Dashboard",
    recordNoun: "Shipment",
    kpiTotalLabel: "Total Shipments",
    kpiSpendLabel: "Total Qty",
    kpiPendingLabel: "Shipments Pending",
    kpiCompletedLabel: "Shipments Completed",
    categoryTitle: "Qty by Category",
  },
};

const STATUS_COLORS = {
  Approved: "#22C55E",
  Completed: "#16A34A",
  Closed: "#16A34A",
  Received: "#16A34A",
  Delivered: "#16A34A",
  Shipped: "#0EA5E9",
  Loaded: "#0EA5E9",
  New: "#3B82F6",
  Open: "#3B82F6",
  Pending: "#F59E0B",
  Partial: "#F97316",
  "In Process": "#6366F1",
  Invoiced: "#0EA5E9",
  "In Transit": "#0EA5E9",
  "On Hold": "#F59E0B",
  Canceled: "#EF4444",
  Cancelled: "#EF4444",
  Standard: "#16A34A",
  Other: "#94A3B8",
};

function toNumber(value) {
  if (value == null || value === "") return 0;
  const n = Number.parseFloat(String(value).replace(/[$,]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function formatMoney(value) {
  const n = toNumber(value);
  return `$${n.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

function formatMoneyExact(value) {
  const n = toNumber(value);
  return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatCompactMoney(value) {
  const n = toNumber(value);
  if (Math.abs(n) >= 1000) {
    const k = n / 1000;
    return `$${k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)}K`;
  }
  return formatMoney(value);
}

function formatDate(value) {
  if (!value) return "N/A";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function shipmentStatusLabel(value) {
  if (value === 0 || `${value}` === "0") return "NEW";
  if (value === 1 || `${value}` === "1") return "OPEN";
  if (value === 2 || `${value}` === "2") return "LOADED";
  if (value === 7 || `${value}` === "7") return "CLOSED";
  if (value === 8 || `${value}` === "8") return "ON HOLD";
  if (value === 9 || `${value}` === "9") return "SHIPPED";
  if (value == null || value === "") return "N/A";
  return String(value).toUpperCase();
}

function poStatusLabel(value) {
  const map = {
    0: "Other",
    1: "New",
    2: "Approved",
    3: "Pending",
    4: "Invoiced",
    5: "Completed",
    6: "Canceled",
    7: "In Process",
  };
  if (value == null || value === "") return "N/A";
  if (map[value] != null) return map[value];
  if (map[String(value)] != null) return map[String(value)];
  const asText = String(value);
  if (/^\d+$/.test(asText) && map[Number(asText)] != null) return map[Number(asText)];
  return asText;
}

function isPendingStatus(status) {
  const s = String(status || "").toLowerCase();
  return /pending|new|open|partial|in process|in transit|on hold|draft|other/.test(s);
}

function isCompletedStatus(status) {
  const s = String(status || "").toLowerCase();
  return /approved|completed|closed|received|delivered|shipped|loaded|invoiced/.test(s);
}

function getLineItems(doc) {
  if (Array.isArray(doc?.lineItems) && doc.lineItems.length) return doc.lineItems;
  if (Array.isArray(doc?.AsnDetail) && doc.AsnDetail.length) {
    return doc.AsnDetail.map((item) => ({
      skuId: item["SKU ID"] || item.skuId,
      description: item.Description || item.description,
      orderedQty: item["Expected Qty"] ?? item.expectedQty,
      receivedQty: item["Received Qty"] ?? item.receivedQty,
      openQty: item.openQty,
      uom: item.UOM || item.uom || "",
      unitPrice: item.unitPrice,
    }));
  }
  if (Array.isArray(doc?.stops) && doc.stops.length) {
    const items = [];
    for (const stop of doc.stops) {
      const lines = Array.isArray(stop?.lineItems) ? stop.lineItems : [];
      for (const li of lines) {
        const detail = li?.detail && typeof li.detail === "object" ? li.detail : {};
        items.push({
          skuId: li.skuId || detail.skuId,
          description: li.description || detail.description,
          orderedQty: li.orderedQty ?? detail.orderedQty ?? li.dispatchQty,
          receivedQty: li.receivedQty ?? detail.receivedQty,
          openQty: li.openQty ?? detail.openQty,
          uom: li.uom || detail.uom || "",
          unitPrice: li.unitPrice ?? detail.unitPrice,
        });
      }
    }
    if (items.length) return items;
  }
  if (Array.isArray(doc?.items) && doc.items.length) return doc.items;
  return [];
}

function normalizeLineItem(li) {
  const detail = li?.detail && typeof li.detail === "object" ? li.detail : {};
  const skuId = li?.skuId || detail.skuId || li?.itemId || detail.itemId || li?.sku || "N/A";
  const qty = toNumber(
    li?.orderedQty ??
      detail.orderedQty ??
      li?.expectedQty ??
      li?.receivedQty ??
      detail.receivedQty ??
      li?.quantity ??
      detail.quantity ??
      0
  );
  const unitPrice = toNumber(li?.unitPrice ?? detail.unitPrice ?? li?.asnUnitPrice ?? 0);
  return {
    skuId: String(skuId),
    description: li?.description || detail.description || li?.name || detail.name || "N/A",
    qty,
    unitPrice,
    unitPriceLabel: unitPrice ? formatMoneyExact(unitPrice) : "—",
    uom: li?.uom || detail.uom || "",
    amount: qty * unitPrice,
  };
}

function categoryFromSku(skuId) {
  const raw = String(skuId || "");
  const match = raw.match(/^([A-Za-z]+)/);
  if (match) return match[1].toUpperCase().slice(0, 4);
  return raw.slice(0, 3).toUpperCase() || "OTHER";
}

function buildCategoryBars(lineItems, useMoney) {
  const map = new Map();
  for (const item of lineItems) {
    const cat = categoryFromSku(item.skuId);
    const prev = map.get(cat) || 0;
    map.set(cat, prev + (useMoney ? item.amount || item.qty * item.unitPrice : item.qty));
  }
  const rows = [...map.entries()]
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 6);
  const max = Math.max(...rows.map((r) => r.value), 1);
  return rows.map((r) => ({
    ...r,
    label: useMoney ? formatCompactMoney(r.value) : String(r.value),
    pct: Math.round((r.value / max) * 100),
  }));
}

function buildStatusPie(statusCounts) {
  const total = Object.values(statusCounts).reduce((a, b) => a + b, 0) || 1;
  return Object.entries(statusCounts).map(([name, count]) => ({
    name,
    value: count,
    pct: Math.round((count / total) * 100),
    color: STATUS_COLORS[name] || STATUS_COLORS[name?.replace(/\b\w/g, (c) => c.toUpperCase())] || "#3B82F6",
  }));
}

function extractPoRecord(doc) {
  const po = doc.purchase_order || {};
  const supplier = doc.supplier || {};
  const lineItems = getLineItems(doc).map(normalizeLineItem);
  const status = poStatusLabel(po.status ?? doc.status);
  const totalAmount = toNumber(po.totalPOPrice ?? doc.totalPOPrice);
  const totalQty = toNumber(po.totalOrderedQuantity) || lineItems.reduce((s, i) => s + i.qty, 0);
  const vendor = supplier.supplier || "N/A";

  return {
    id: po.orderId || doc.orderId || "PO",
    status,
    amount: totalAmount,
    qty: totalQty,
    party: vendor,
    date: formatDate(po.purchaseOrderDate),
    summary: [
      { label: "PO Number", value: po.orderId || doc.orderId || "N/A", accent: true },
      { label: "Vendor", value: vendor },
      { label: "Order Date", value: formatDate(po.purchaseOrderDate) },
      { label: "Expected Delivery", value: formatDate(po.expectedReceiptDate) },
      { label: "Total Amount", value: formatMoney(totalAmount) },
      { label: "Status", value: status, status: true },
      { label: "Items", value: String(lineItems.length || po.totalLineItems || 0) },
      { label: "Total Qty", value: String(totalQty) },
    ],
    lineItems,
    useMoneyCategory: true,
  };
}

function extractAsnRecord(doc) {
  const asn = doc.asnDetails || doc.asn || doc;
  const lineItems = getLineItems(doc).map(normalizeLineItem);
  const status = String(asn.receiptStatus || asn.status || "N/A");
  const totalAmount = toNumber(asn.totalASNPrice || asn.totalPOValue);
  const totalQty = toNumber(asn.totalExpectedQty) || lineItems.reduce((s, i) => s + i.qty, 0);
  const party = doc.shipFrom?.supplierReference || "N/A";

  return {
    id: asn.asnNumber || doc.asnNumber || "ASN",
    status,
    amount: totalAmount,
    qty: totalQty,
    party,
    date: formatDate(asn.asnDate || asn.createdAt || asn.expectedReceiptDate),
    summary: [
      { label: "ASN Number", value: asn.asnNumber || doc.asnNumber || "N/A", accent: true },
      { label: "Supplier Ref", value: party },
      { label: "Warehouse", value: asn.warehouse || asn.warehouseId || "N/A" },
      { label: "Expected Qty", value: String(asn.totalExpectedQty ?? totalQty) },
      { label: "Received Qty", value: String(asn.totalReceivedQty ?? "N/A") },
      { label: "Status", value: status, status: true },
      { label: "Total Value", value: formatMoney(totalAmount) },
      { label: "Items", value: String(lineItems.length) },
    ],
    lineItems,
    useMoneyCategory: true,
  };
}

function extractGrnRecord(doc) {
  const header = doc.AsnHeader || doc;
  const lineItems = getLineItems(doc).map(normalizeLineItem);
  const status = header.Type === 1 || header.Type === "1" ? "Standard" : String(header.Type || "Received");
  const expected = toNumber(header["Total Expected Qty"]);
  const received = toNumber(header["Total Received Qty"]);
  const totalQty = received || expected || lineItems.reduce((s, i) => s + i.qty, 0);
  const party = header.Owner || header["Owner"] || "N/A";

  return {
    id: header["GRN Number"] || header.grnNumber || "GRN",
    status,
    amount: totalQty,
    qty: totalQty,
    party,
    date: formatDate(header["Created Date"]),
    summary: [
      { label: "GRN Number", value: header["GRN Number"] || header.grnNumber || "N/A", accent: true },
      { label: "ASN Number", value: header["ASN Number"] || header.asnNumber || "N/A" },
      { label: "Facility", value: header["Facility Name"] || header.facilityName || "N/A" },
      { label: "Owner", value: party },
      { label: "Expected Qty", value: String(header["Total Expected Qty"] ?? "N/A") },
      { label: "Received Qty", value: String(header["Total Received Qty"] ?? totalQty), status: true },
      { label: "Created Date", value: formatDate(header["Created Date"]) },
      { label: "Items", value: String(lineItems.length) },
    ],
    lineItems,
    useMoneyCategory: false,
  };
}

function extractSoRecord(doc) {
  const so = doc.soDetails || doc.sales_order || doc.salesOrder || {};
  const invoice = doc.invoice || {};
  const lineItems = getLineItems(doc).map(normalizeLineItem);
  const status = poStatusLabel(so.status ?? doc.status);
  // Sales may reuse numeric enums similar to PO; fall back to raw text if unknown.
  const statusLabel =
    status !== String(so.status ?? doc.status) || !/^\d+$/.test(String(so.status ?? ""))
      ? status
      : String(so.status ?? doc.status ?? "N/A");
  const totalAmount = toNumber(invoice.invoiceAmount || so.totalPrice || so.totalSOPrice);
  const totalQty = toNumber(so.totalOrderedQuantity) || lineItems.reduce((s, i) => s + i.qty, 0);
  const party = so.owner || doc.destination?.destinationName || "N/A";

  return {
    id: so.soNumber || so.orderId || so.orderNumber || "SO",
    status: statusLabel,
    amount: totalAmount,
    qty: totalQty,
    party,
    date: formatDate(so.orderDate || so.salesOrderDate),
    summary: [
      { label: "SO Number", value: so.soNumber || so.orderId || so.orderNumber || "N/A", accent: true },
      { label: "Customer", value: party },
      { label: "Order Date", value: formatDate(so.orderDate || so.salesOrderDate) },
      { label: "Warehouse", value: doc.warehouse || so.warehouse || "N/A" },
      { label: "Total Amount", value: formatMoney(totalAmount) },
      { label: "Status", value: statusLabel, status: true },
      { label: "Items", value: String(lineItems.length) },
      { label: "Total Qty", value: String(totalQty) },
    ],
    lineItems,
    useMoneyCategory: true,
  };
}

function extractShipmentRecord(doc) {
  const hdr = doc.shipmentDetails || doc.header || {};
  const src = doc.sourceDetail || {};
  const dest = doc.destination || {};
  const carr = doc.carrier || {};
  const load = doc.load || doc.loadDates || {};
  const lineItems = getLineItems(doc).map(normalizeLineItem);
  const status = shipmentStatusLabel(doc.status ?? hdr.status);
  const totalQty = toNumber(hdr.totalQty ?? hdr.totalOrderedQuantity) || lineItems.reduce((s, i) => s + i.qty, 0);
  const party = dest.destinationName || dest.name || dest.city || "N/A";

  return {
    id: hdr.shipmentNumber || doc.shipmentNumber || doc.shipmentOrder || "SHIP",
    status,
    amount: totalQty,
    qty: totalQty,
    party,
    date: formatDate(hdr.shipmentDate || doc.createdAt),
    summary: [
      { label: "Shipment No", value: hdr.shipmentNumber || doc.shipmentNumber || "N/A", accent: true },
      { label: "Source", value: hdr.source || src.sourceName || src.name || "N/A" },
      { label: "Destination", value: party },
      { label: "Carrier", value: carr.carrierName || "N/A" },
      { label: "Total Qty", value: String(totalQty) },
      { label: "Status", value: status, status: true },
      { label: "Shipment Date", value: formatDate(hdr.shipmentDate || doc.createdAt) },
      { label: "Expected Arrival", value: formatDate(load.expectedArrivalDate || hdr.expectedArrivalDate) },
    ],
    lineItems,
    useMoneyCategory: false,
  };
}

function extractRecord(doc, collection) {
  if (collection === "grns" || doc?.AsnHeader || doc?.AsnDetail) return extractGrnRecord(doc);
  if (collection === "shipments" || doc?.shipmentDetails) return extractShipmentRecord(doc);
  if (collection === "purchaseorders" || doc?.purchase_order) return extractPoRecord(doc);
  if (collection === "salesorders" || doc?.soDetails || doc?.sales_order || doc?.salesOrder) {
    return extractSoRecord(doc);
  }
  if (collection === "asns" || doc?.asnDetails) return extractAsnRecord(doc);
  return null;
}

function buildMultiSummary(records, collection, meta) {
  const totalAmount = records.reduce((s, r) => s + (r.amount || 0), 0);
  const totalQty = records.reduce((s, r) => s + (r.qty || 0), 0);
  const totalItems = records.reduce((s, r) => s + (r.lineItems?.length || 0), 0);
  const parties = [...new Set(records.map((r) => r.party).filter((p) => p && p !== "N/A"))];
  const statuses = [...new Set(records.map((r) => r.status).filter(Boolean))];
  const useMoney = collection !== "grns" && collection !== "shipments";

  const idLabel =
    collection === "purchaseorders"
      ? "PO Numbers"
      : collection === "asns"
        ? "ASN Numbers"
        : collection === "grns"
          ? "GRN Numbers"
          : collection === "salesorders"
            ? "SO Numbers"
            : "Shipment Nos";

  const partyLabel =
    collection === "salesorders"
      ? "Customers"
      : collection === "shipments"
        ? "Destinations"
        : collection === "grns"
          ? "Owners"
          : "Vendors";

  return [
    { label: idLabel, value: records.map((r) => r.id).join(", "), accent: true },
    { label: partyLabel, value: parties.length ? parties.join(", ") : "N/A" },
    { label: "Orders", value: String(records.length) },
    { label: useMoney ? "Total Amount" : "Total Qty", value: useMoney ? formatMoney(totalAmount) : String(Math.round(totalQty)) },
    { label: "Total Items", value: String(totalItems) },
    { label: "Statuses", value: statuses.join(", ") || "N/A", status: true },
    { label: "Module", value: meta.recordNoun },
    { label: "Mode", value: "Multi Order · Aggregated" },
  ];
}

/**
 * @param {object[]} docs
 * @param {string} collection
 * @returns {object|null}
 */
export function buildModuleDashboard(docs, collection) {
  const meta = MODULE_META[collection];
  if (!meta || !Array.isArray(docs) || !docs.length) return null;

  // De-dupe by record id (multi-id fetches can overlap).
  const seen = new Set();
  const records = [];
  for (const doc of docs) {
    const rec = extractRecord(doc, collection);
    if (!rec) continue;
    const key = String(rec.id || "").toUpperCase();
    if (key && seen.has(key)) continue;
    if (key) seen.add(key);
    records.push(rec);
  }
  if (!records.length) return null;

  const isMulti = records.length > 1;
  const primary = records[0];
  const statusCounts = {};
  let totalSpend = 0;
  let pending = 0;
  let completed = 0;
  const allLineItems = [];

  for (const rec of records) {
    const statusKey = rec.status || "N/A";
    statusCounts[statusKey] = (statusCounts[statusKey] || 0) + 1;
    totalSpend += rec.amount || 0;
    if (isPendingStatus(rec.status)) pending += 1;
    if (isCompletedStatus(rec.status)) completed += 1;
    for (const li of rec.lineItems || []) {
      allLineItems.push({
        ...li,
        orderId: rec.id,
      });
    }
  }

  const useMoney = primary.useMoneyCategory !== false && collection !== "grns" && collection !== "shipments";
  const statusPie = buildStatusPie(statusCounts);
  const categoryBars = buildCategoryBars(allLineItems.length ? allLineItems : primary.lineItems, useMoney);

  const kpiSpendValue =
    collection === "grns" || collection === "shipments"
      ? String(Math.round(totalSpend))
      : formatMoney(totalSpend);

  const title = isMulti
    ? `${meta.title} · ${records.length} Orders`
    : `${meta.title} - ${primary.id}`;

  const summary = isMulti ? buildMultiSummary(records, collection, meta) : primary.summary;

  return {
    module: meta.key,
    collection,
    title,
    subtitle: `${isMulti ? "Multi Order" : "Single Order"} · ${records.length} record(s) · Realtime`,
    live: true,
    isMulti,
    summary,
    records: records.map((r) => ({
      id: r.id,
      party: r.party || "N/A",
      date: r.date || "N/A",
      status: r.status || "N/A",
      amount: useMoney ? formatMoney(r.amount) : String(Math.round(r.qty || 0)),
      qty: r.qty,
      items: r.lineItems?.length || 0,
    })),
    kpis: [
      { label: meta.kpiTotalLabel, value: String(records.length), tone: "blue" },
      { label: meta.kpiSpendLabel, value: kpiSpendValue, tone: "green" },
      { label: meta.kpiPendingLabel, value: String(pending), tone: "orange" },
      { label: meta.kpiCompletedLabel, value: String(completed), tone: "teal" },
    ],
    statusPie,
    categoryTitle: meta.categoryTitle,
    categoryBars,
    lineItems: allLineItems.map((li) => ({
      orderId: li.orderId || primary.id,
      sku: li.skuId,
      description: li.description,
      qty: li.qty,
      unitPrice: li.unitPriceLabel,
    })),
    lineItemCount: allLineItems.length,
    showOrderColumn: isMulti,
  };
}

export function canBuildDashboard(collection) {
  return Boolean(MODULE_META[collection]);
}
